import React from "react";
import { Icon, Portal, notify, autoSchedule, addDays, nextWeekday, workdaysOf, ymd as calYmd, useDragPosition, woEstLabor, actualHours, crewRate } from "./registry";
import { logoToDataUrl } from "./lib/img";
import { DEMO_SAMPLE_YARDS } from "./lib/demoseed";
import { GUIDE } from "./pageguide";
/* ============================================================
   Fence Works — the sandbox's guided walkthrough: one job, start to finish
   1 lead → 2 quote on the map → 3 quote sheet → 4 customer portal → 5 deposit
   → work order → 6 order materials → 7 auto-schedule → 8 site readiness +
   change order → 9 install day (Crew Mode) → 10 photos + complete → 11 final
   payment → 12 review + referral → 13 Business Health. Runs ON TOP of the real UI: a spotlight
   ring on the element that matters and a card with the copy + controls.
   The card never fully closes: "Step N of 12" jumps anywhere (fast-forwarding
   the job as needed), and wandering off the step shows that page's guide.
   ============================================================ */
const D = (): any => (window as any).VFP_DEMO || { active: false };
const LEAD_Q = "q-demo-lead";
const LEAD_C = "cust-demo-lead";
const STEPS = [
  { n: 1, title: "A lead is waiting", route: "dashboard" },
  { n: 2, title: "Quote your own address", route: "estimator" },
  { n: 3, title: "Your quote sheet", route: "quote" },
  { n: 4, title: "What your customer gets", route: "quote" },
  { n: 5, title: "Deposit paid → work order", route: "workorders" },
  { n: 6, title: "Order materials", route: "workorders" },
  { n: 7, title: "It schedules itself", route: "calendar" },
  { n: 8, title: "Site readiness + a change order", route: "workorders" },
  { n: 9, title: "Install day", route: "calendar" },   // schedule first, then Crew Mode (tour.installPhase)
  { n: 10, title: "Photos, then done", route: "workorders" },
  { n: 11, title: "Final payment", route: "quote" },
  { n: 12, title: "Review + referral", route: "workorders" },
  { n: 13, title: "Business Health", route: "reports" },
];
const N = STEPS.length;
/* step 9 is two pages: the schedule (where the job landed) then Crew Mode */
const routeOf = (st: any, tour: any) => (st.n === 9 && tour.installPhase === "crew" ? "crew" : st.route);
const CO_PHOTO = "/sample-photos/yard-before.svg";
const DAY = 86400000;
const ymd = (t: number) => { const d = new Date(t); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
const today = () => ymd(Date.now());

function hex(n: number) { const a = new Uint8Array(n); try { crypto.getRandomValues(a); } catch (e) { for (let i = 0; i < n; i++) a[i] = (Math.random() * 256) | 0; } return Array.from(a).map((b) => b.toString(16).padStart(2, "0")).join(""); }
const money0 = (n: any) => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");
const fmtDay = (s: string) => { const d = new Date(s + "T12:00:00"); return isNaN(d.getTime()) ? s : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); };
const SAMPLE_PHOTOS = [
  { name: "before-yard.svg", url: "/sample-photos/yard-before.svg", tag: "before" },
  { name: "after-fence.svg", url: "/sample-photos/yard-after.svg", tag: "after" },
  { name: "after-gate.svg", url: "/sample-photos/yard-gate.svg", tag: "after" },
];

/* Which match to point at. A control can exist twice — the estimator renders
   "Generate Estimate" both in its dock and in the phone action bar — so prefer
   the one actually on screen. Note offsetParent is null for a position:fixed
   element, which is why the phone bar's copy used to be skipped and the ring
   landed on the dock button 4,800px down the page. */
function pickTarget(selector: string) {
  const all: any[] = Array.from(document.querySelectorAll(selector));
  const shown = all.filter((x: any) => x.getClientRects().length > 0);
  const vh = window.innerHeight || 0, vw = window.innerWidth || 0;
  const onScreen = shown.find((x: any) => { const r = x.getBoundingClientRect(); return r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw; });
  return onScreen || shown[0] || all[0] || null;
}

/* the spotlight: a ring on one element (no overlay — nothing is blocked) */
function useSpotlight(selector: string | null, deps: any[]) {
  React.useEffect(() => {
    if (!selector) return;
    let el: any = null, tries = 0;
    const find = () => {
      el = pickTarget(selector);
      if (!el) { if (tries++ < 20) setTimeout(find, 150); return; }
      el.classList.add("demo-spot");
      try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (e) {}
    };
    find();
    return () => { if (el) el.classList.remove("demo-spot"); };
  }, deps);
}

/* the phone frame: the customer hub for THIS session's token, live */
function PhoneFrame({ token, compact }: any) {
  const src = "/hub/" + encodeURIComponent(token) + "?demo_frame=1";
  if (compact) {
    return <a className="btn btn-primary btn-block" href={src.replace("?demo_frame=1", "")} target="_blank" rel="noopener" onClick={() => D().track("customer_view_opened", { via: "tab" })}><Icon name="phone" size={14} />Open the customer view ↗</a>;
  }
  return (
    <div className="demo-phone">
      <div className="demo-phone-notch" />
      <iframe title="Your customer's view" src={src} onLoad={() => D().track("customer_view_opened", { via: "frame" })} />
    </div>
  );
}

/* step 3: put your company on it (optional). Logo is validated — a bad
   upload falls back to text branding instead of a broken image. */
function BrandingFields({ store }: any) {
  const d = D();
  const co = store.company || {};
  const seeded = String(co.name || "") === d.seedCompany;
  const [name, setName] = React.useState<any>(seeded ? "" : (co.name || ""));
  const [logoErr, setLogoErr] = React.useState<any>("");
  const fileRef = React.useRef<any>(null);
  const commitName = () => {
    const n = String(name || "").trim();
    if (!n || n === co.name) return;
    store.setCompany({ name: n }); store.setSettings({ companyName: n });
    d.setCompany(n); d.track("branding_added", { kind: "name" });
  };
  async function pickLogo(file: File) {
    setLogoErr("");
    try {
      if (!/^image\/(png|jpeg|svg\+xml)$/.test(file.type)) throw new Error("PNG, JPG or SVG only");
      if (file.size > 1.5 * 1024 * 1024) throw new Error("Keep the logo under 1.5 MB");
      const url = await logoToDataUrl(file);
      const ok = await new Promise<boolean>((res) => { const im = new Image(); im.onload = () => res(im.naturalWidth >= 48 && im.naturalHeight >= 24); im.onerror = () => res(false); im.src = url; });
      if (!ok) throw new Error("That image is too small to print — text branding is used instead");
      store.setCompany({ logo: url }); d.track("branding_added", { kind: "logo" });
    } catch (e: any) { setLogoErr(String((e && e.message) || "Couldn't use that image — text branding is used instead")); }
  }
  return (
    <div className="demo-brand">
      <div className="kicker" style={{ marginBottom: 6 }}>Put your company on it <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>· optional</span></div>
      <div className="row gap-8" style={{ flexWrap: "wrap" }}>
        <input className="input" style={{ flex: "1 1 160px" }} placeholder="Your company name" value={name} onChange={(e) => setName(e.target.value)} onBlur={commitName} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitName(); (e.target as HTMLInputElement).blur(); } }} />
        <button className="btn btn-outline btn-sm" type="button" onClick={() => fileRef.current && fileRef.current.click()}><Icon name="image" size={13} />{co.logo ? "Replace logo" : "Add logo"}</button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" style={{ display: "none" }} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) pickLogo(f); e.currentTarget.value = ""; }} />
      </div>
      {co.logo && <div className="row gap-8" style={{ marginTop: 8, alignItems: "center" }}><img src={co.logo} alt="" style={{ height: 28, maxWidth: 120, objectFit: "contain" }} /><span className="faint" style={{ fontSize: 11.5 }}>On the quote and the customer's email.</span></div>}
      {logoErr && <p style={{ color: "var(--amber)", fontSize: 12, margin: "6px 0 0" }}>{logoErr}</p>}
    </div>
  );
}

/* Founder line, priced from the live plan table (account.tsx PLANS) so a
   repricing can never leave stale numbers in the demo. Scarcity is seats now,
   not a date, so this doesn't quote a deadline. */
function founderLine() {
  const w: any = window as any;
  const plan = w.PLAN ? w.PLAN("pro") : null;
  const full = plan && plan.full;
  const half = full && w.founder ? w.founder(full) : null;
  if (!full || !half) return "Founder pricing locks 50% off for life while founder seats last.";
  return `Founder rate: ${plan.name} $${half}/mo instead of $${full}, locked for life while founder seats last.`;
}

/* the lead widget snippet, shown from the end card */
function WidgetSnippetModal({ onClose, onKeep, onFounder }: any) {
  const url = (location.hostname === "localhost" ? location.origin : "https://app.fenceworks.ai") + "/widget/YOUR-TOKEN";
  const embed = `<!-- Fence Works instant estimator -->\n<iframe src="${url}"\n  style="width:100%;max-width:520px;height:640px;border:0;border-radius:16px"\n  title="Get a fence estimate" loading="lazy"></iframe>`;
  const [copied, setCopied] = React.useState<any>(false);
  return (
    <Portal>
      <div className="modal-scrim" data-dialog onClick={onClose}>
        <div className="modal" style={{ width: "min(560px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
          <h3 className="display" style={{ fontSize: 19, margin: "0 0 6px" }}>Install the lead widget on your site</h3>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 12px" }}>Paste this into any page — Squarespace, Wix, WordPress, raw HTML. Homeowners get an instant estimate; the lead lands in your inbox exactly like the one you just quoted. Your real token replaces <b>YOUR-TOKEN</b> the moment you start a free workspace.</p>
          <pre className="embed-code">{embed}</pre>
          <div className="row gap-8" style={{ marginTop: 12 }}>
            <button className="btn btn-outline btn-sm" onClick={() => { try { navigator.clipboard.writeText(embed); } catch (e) {} setCopied(true); setTimeout(() => setCopied(false), 1800); }}>{copied ? "Copied ✓" : "Copy"}</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
          {/* the snippet is useless without a workspace to mint the real token —
              so the two ways to get one live right here too */}
          {(onKeep || onFounder) && <div className="demo-end" style={{ marginTop: 14 }}>
            <p className="faint" style={{ margin: 0, textAlign: "center" }}>Start a workspace and <b>YOUR-TOKEN</b> becomes your real one.</p>
            {onKeep && <button className="btn btn-primary btn-block" onClick={onKeep}><Icon name="check" size={14} />Keep this as my free workspace</button>}
            {onFounder && <button className="btn btn-outline btn-block" onClick={onFounder}><Icon name="bolt" size={14} />Lock founder pricing — 50% off forever</button>}
            <p className="faint" style={{ margin: "2px 0 0", textAlign: "center" }}>{founderLine()}</p>
          </div>}
        </div>
      </div>
    </Portal>
  );
}

/* ---- the job the visitor is running (Maria's lead) — readers + fast-forward ----
   Each step has a `ready` check; jumping past an unmet step applies the
   minimum store changes to get there (what the visitor would have done). */
function jobOf(store: any, tour: any) {
  const q = (store.quotes || []).find((x: any) => x.id === (tour.quoteId || LEAD_Q)) || null;
  const cust = q && ((store.customers || []).find((c: any) => c.id === q.customerId) || (store.customers || []).find((c: any) => c.id === LEAD_C));
  const wo = q && (store.workOrders || []).find((w: any) => w.quoteId === q.id);
  return { q, cust, wo };
}
function paidOf(q: any) { return q ? (Number(q.depositPaid) || 0) + (q.payments || []).reduce((s: number, p: any) => s + (Number(p.amount) || 0), 0) : 0; }
const SCHEDULED = new Set(["scheduled", "in_progress", "completed"]);
function readyFor(n: number, store: any, tour: any) {
  const { q, cust, wo } = jobOf(store, tour);
  const drawn = !!(q && q.inputs && (q.inputs.parcel || (q.inputs.mVerts && q.inputs.mVerts.length > 1)));
  const paid = !!(q && q.status === "paid");
  const balanceLeft = q ? Math.max(0, (Number(q.total) || 0) - paidOf(q)) : 0;
  switch (n) {
    case 1: case 2: return true;
    case 3: return drawn;
    case 4: return drawn && !!(cust && cust.hubToken);
    case 5: return paid && !!wo;
    case 6: return !!wo;
    case 7: return !!(wo && wo.suppliesOrdered && wo.materialEta);
    case 8: return !!(wo && wo.scheduledStart && SCHEDULED.has(wo.status));
    case 9: return !!(wo && wo.scheduledStart && SCHEDULED.has(wo.status) && wo.readiness && wo.readiness.siteVisit && wo.readiness.permit && wo.readiness.diggers);
    case 10: return !!(wo && (wo.status === "in_progress" || wo.status === "completed"));
    case 11: return !!(wo && wo.status === "completed");
    case 12: return !!(wo && wo.status === "completed" && balanceLeft < 0.5);
    case 13: return !!(wo && wo.status === "completed" && balanceLeft < 0.5 && wo.reviewRequested);
    default: return true;
  }
}
/* apply what step n needs, idempotently (never touches the seeded jobs) */
function fastForward(n: number, store: any, tour: any) {
  const d = D();
  const { q, cust, wo } = jobOf(store, tour);
  if (!q) return;
  const VFP: any = (window as any).VFP;
  const catalog = store.allProducts || VFP.PRODUCTS;
  const product = catalog.find((p: any) => p.id === q.productId) || catalog[0];
  if (n === 3 && !readyFor(3, store, tour)) {
    const yard = DEMO_SAMPLE_YARDS[0];
    const lot = yard && yard.lot;
    if (lot) {
      const inputs = { ...lot, productId: product.id, corners: 2, removal: "none", permit: true };
      let total = q.total;
      try { total = Math.round(VFP.computeQuote(inputs, product, store.settings).customerTotal); } catch (e) {}
      store.updateQuote(q.id, { inputs, lf: inputs.linearFeet, total, productName: product.name, productId: product.id, address: yard.address });
      d.track("fence_drawn", { via: "fast_forward" });
    }
  }
  if (n === 4 && cust && !cust.hubToken) store.updateCustomer(cust.id, { hubToken: "demo-" + hex(12) });
  if (n === 4 && cust && !((store.threads || {})[cust.id] || []).length) seedPortalChat(store, q, cust);
  if (n === 5 && q.status !== "paid") {
    const total = Number(q.total) || 0;
    const dep = Math.round(total * 0.5 * 100) / 100;
    store.updateQuote(q.id, { status: "paid", signature: q.signature && q.signature.at ? q.signature : { name: q.customer, at: new Date().toISOString(), terms: "fw-terms-v1", sandbox: true }, signedTotal: q.signedTotal != null ? q.signedTotal : total, depositPaid: (Number(q.depositPaid) || 0) + dep, stripePayments: [...(q.stripePayments || []), { amount: dep, method: "stripe", source: "sandbox", kind: "deposit", at: Date.now() }] });
    d.track("deposit_mocked", { via: "fast_forward" });
    // the work order is created by App's paid → work-order effect on the next render
  }
  if (!wo) return;
  if (n === 6 && !(wo.suppliesOrdered && wo.materialEta)) {
    // same as the work order's "Materials in — schedule tentatively after …" button
    const eta = ymd(Date.now() + 3 * DAY);
    const tentative = ymd(Date.now() + 4 * DAY);
    store.updateWorkOrder(wo.id, { suppliesOrdered: true, materialEta: eta, status: wo.status === "materials" ? "unscheduled" : wo.status, scheduledStart: wo.scheduledStart || tentative, scheduledEnd: wo.scheduledEnd || tentative });
  }
  if (n === 7 && !SCHEDULED.has(wo.status)) {
    // the real auto-scheduler: next open crew workday after the materials ETA
    if (wo.status !== "unscheduled") store.updateWorkOrder(wo.id, { status: "unscheduled" });
    // only this job is offered to the scheduler — the seeded jobs stay as they are
    try { autoSchedule({ ...store, workOrders: (store.workOrders || []).filter((w: any) => w.id === wo.id || w.status === "scheduled").map((w: any) => w.id === wo.id ? { ...w, status: "unscheduled" } : w) }); }
    catch (e) {
      const start = nextWeekday(addDays(new Date(), 1), workdaysOf(store));
      store.updateWorkOrder(wo.id, { status: "scheduled", scheduledStart: calYmd(start), scheduledEnd: calYmd(start), crewId: wo.crewId || (store.settings && store.settings.crews && store.settings.crews[0] && store.settings.crews[0].id) });
    }
  }
  if (n === 8 && !(wo.readiness && wo.readiness.siteVisit && wo.readiness.permit && wo.readiness.diggers)) store.updateWorkOrder(wo.id, { readiness: { ...(wo.readiness || {}), siteVisit: true, permit: true, permitDoc: "permit-approved.pdf", diggers: true, diggersAt: Date.now() } });
  if (n === 9 && wo.status !== "in_progress" && wo.status !== "completed") store.updateWorkOrder(wo.id, { status: "in_progress", scheduledStart: wo.scheduledStart || today() });
  if (n === 10 && wo.status !== "completed") {
    const photos = (wo.photos || []).length ? wo.photos : SAMPLE_PHOTOS.map((p) => ({ ...p, at: Date.now() }));
    store.updateWorkOrder(wo.id, { photos, status: "completed", timeLogs: demoTimeLogs(wo, store) });
  }
  if (n === 11) {
    const left = Math.round(((Number(q.total) || 0) - paidOf(q)) * 100) / 100;
    if (left > 0.5) store.updateQuote(q.id, { depositPaid: (Number(q.depositPaid) || 0) + left, stripePayments: [...(q.stripePayments || []), { amount: left, method: "stripe", source: "sandbox", kind: "balance", at: Date.now() }] });
  }
  if (n === 12 && !wo.reviewRequested) store.updateWorkOrder(wo.id, { reviewRequested: true, reviewRequestedAt: Date.now() });
}

/* the crew's day, as it would be clocked: the estimate's hours plus about half
   an hour (the extra run to the shed, a hardware swap) — so est vs actual has
   something to say on the work order, the invoice breakdown and Reports */
function demoTimeLogs(wo: any, store: any) {
  const logs = Array.isArray(wo.timeLogs) ? wo.timeLogs : [];
  const crews = (store.settings && store.settings.crews) || [];
  const est = woEstLabor(wo, store, crews);
  const have = actualHours(wo);
  const target = Math.round((Math.max(0.5, est.hours) + 0.5) * 4) / 4;
  const add = Math.round((target - have) * 4) / 4;
  if (add < 0.25) return logs;
  const day = wo.scheduledStart || today();
  const inAt = new Date(day + "T07:12:00").getTime(), outAt = inAt + add * 3600000;
  return [...logs, { id: "tl-demo-" + Date.now(), hours: add, date: day, crewId: wo.crewId, clockInAt: inAt, clockOutAt: outAt, createdAt: Date.now(),
    note: "Ran about half an hour over: the extra 20 ft to the shed needed two more post holes, and the walk-gate latch was swapped for a self-closing one.",
    gps: { ok: true, distMi: 0.03, accuracyM: 12 } }];
}

/* the message {custFirst} sends from her portal before she pays, and the
   office's reply — the thread lives on Messages like a real one would */
function seedPortalChat(store: any, q: any, cust: any) {
  if (!store || !store.addMessage || !cust) return;
  const first = String((q && q.customer) || cust.name || "").split(" ")[0] || "Maria";
  const total = Number(q && q.total) || 0;
  const dep = Math.round(total * 0.5);
  const now = Date.now();
  store.addMessage(cust.id, { from: "customer", text: `Hi! Just read through the quote — looks good. Is the deposit half, and can I pay it by card? Also when could you start?`, at: now - 6 * 60000 });
  store.addMessage(cust.id, { from: "company", text: `Hi ${first} — yes, the deposit is 50% (${money0(dep)}) and it holds your spot; card or bank transfer both work on the link. Balance is due when we're done. Materials take about 3 days, then you're on the next open crew day. Sign + pay on the link and I'll get the order in today.`, at: now - 2 * 60000 });
}

/* a page the visitor wandered to (not the step's page) — its guide, from
   the same copy the real app's "?" shows, plus the sandbox note */
function PageContext({ route, step, onBack }: any) {
  const g = GUIDE[route];
  if (!g) return null;
  return (
    <>
      <p><b>{g.title}.</b> {g.what}</p>
      <ul className="demo-how">{g.how.slice(0, 3).map((h: string, i: number) => <li key={i}>{h}</li>)}</ul>
      {g.sandbox && <p className="faint"><b>In the sandbox:</b> {g.sandbox}</p>}
      <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={onBack}>← Back to step {step.n}: {step.title}</button></div>
    </>
  );
}

function DemoTour({ store, route, params, go }: any) {
  const d = D();
  const [tour, setTourState] = React.useState<any>(() => Object.assign({ step: 1, dismissed: false, done: false }, d.tour || {}));
  const setTour = (patch: any) => setTourState((t: any) => { const n = { ...t, ...patch }; d.setTour(n); return n; });
  React.useEffect(() => { if (!d.tour) d.setTour(tour); }, []);
  const [lf, setLf] = React.useState<any>(0);
  const [est, setEst] = React.useState<any>({ house: false, houseFitAt: 0, sideMenuAt: 0, gates: 0 });
  const [email, setEmail] = React.useState<any>(d.email || "");
  const [sendState, setSendState] = React.useState<any>("");
  const [snippet, setSnippet] = React.useState<any>(false);
  const [list, setList] = React.useState<any>(false);
  const [collapsed, setCollapsed] = React.useState<any>(false);
  const narrow = typeof window !== "undefined" && window.innerWidth < 760;
  // The estimator pins its own "Generate Estimate" bar to the bottom of a phone
  // screen, and this sheet is fixed to that same edge at a higher z-index — so
  // the one button step 2 tells you to press was underneath it, spotlight and
  // all. Measure the bar so the sheet can rest above it instead of burying it.
  const [actionBarH, setActionBarH] = React.useState<any>(0);
  React.useEffect(() => {
    if (!narrow) { setActionBarH(0); return; }
    const read = () => {
      const el: any = document.querySelector(".est-mobile-actions");
      const h = el && el.getClientRects().length ? Math.round(el.getBoundingClientRect().height) : 0;
      setActionBarH((prev: number) => (prev === h ? prev : h));
    };
    read();
    const t = setInterval(read, 700);   // the bar mounts with the estimator, after this card
    window.addEventListener("resize", read);
    return () => { clearInterval(t); window.removeEventListener("resize", read); };
  }, [narrow, route]);
  // the card and the minimized pill are movable (grip / header) so they never sit on a feature
  const cardDrag = useDragPosition("fw_demo_card_pos", { disabled: narrow });
  const pillDrag = useDragPosition("fw_demo_pill_pos");
  const step = STEPS[Math.min(N, Math.max(1, tour.step)) - 1];
  const stepRoute = routeOf(step, tour);
  const { q: leadQuote, cust: leadCust, wo: woForQuote } = jobOf(store, tour);
  const custFirst = leadQuote && leadQuote.customer ? String(leadQuote.customer).split(" ")[0] : "your customer";
  const total = leadQuote ? money0(leadQuote.total) : "";
  const balance = leadQuote ? Math.max(0, (Number(leadQuote.total) || 0) - paidOf(leadQuote)) : 0;

  // step 2: watch the estimator's live footage + the coaching marks
  React.useEffect(() => {
    if (tour.step !== 2 || route !== "estimator") return;
    const t = setInterval(() => {
      const e = d.estimator; const v = e && e.lf ? Number(e.lf()) || 0 : 0;
      setLf((p: number) => { if (p < 1 && v >= 1) d.track("fence_drawn", { lf: Math.round(v) }); return v; });
      if (e) setEst({ house: !!(e.house && e.house()), houseFitAt: (e.houseFitAt && e.houseFitAt()) || 0, sideMenuAt: (e.sideMenuAt && e.sideMenuAt()) || 0, gates: (e.gates && e.gates()) || 0 });
    }, 400);
    return () => clearInterval(t);
  }, [tour.step, route]);
  React.useEffect(() => {
    if (!narrow) { setCollapsed(false); return; }
    setCollapsed(tour.step === 2 && route === "estimator" && lf >= 1);
  }, [narrow, tour.step, route, lf >= 1]);
  // step 2 → 3: the estimate got generated
  React.useEffect(() => {
    if (tour.step === 2 && route === "quote" && params && params.quoteId) { d.track("quote_built", { quote: params.quoteId === LEAD_Q ? "lead" : "new" }); setTour({ step: 3, quoteId: params.quoteId }); }
  }, [route, params && params.quoteId]);
  // step 4: the portal link exists the moment the step opens (that IS the step),
  // and {custFirst}'s question lands on Messages — no form to fill first
  React.useEffect(() => {
    if (tour.step !== 4 || !leadCust || !readyFor(3, store, tour)) return;
    if (!leadCust.hubToken) store.updateCustomer(leadCust.id, { hubToken: "demo-" + hex(12) });
    else if (!tour.hubToken) setTour({ hubToken: leadCust.hubToken });
    if (!((store.threads || {})[leadCust.id] || []).length) seedPortalChat(store, leadQuote, leadCust);
  }, [tour.step, leadCust && leadCust.hubToken]);
  // auto-advance on the visitor's own actions
  React.useEffect(() => {
    // `fromRoute` = the page the visitor was on when the step advanced on its
    // own; the new step's card shows there (with an "Open …" button) instead
    // of that page's guide, so the flow never loses them
    const adv = (n: number) => setTour({ step: n, fromRoute: route });
    if (tour.step === 4 && leadQuote && leadQuote.status === "paid") { d.track("work_order_created", { via: "hub" }); adv(5); }
    if (tour.step === 6 && woForQuote && woForQuote.suppliesOrdered && woForQuote.materialEta) adv(7);
    if (tour.step === 7 && woForQuote && woForQuote.scheduledStart && SCHEDULED.has(woForQuote.status)) adv(8);
    if (tour.step === 9 && woForQuote && (woForQuote.status === "in_progress" || woForQuote.status === "completed")) adv(10);
    if (tour.step === 10 && woForQuote && woForQuote.status === "completed") adv(11);
    if (tour.step === 11 && woForQuote && woForQuote.status === "completed" && balance < 0.5) adv(12);
  }, [tour.step, leadQuote && leadQuote.status, woForQuote && woForQuote.suppliesOrdered, woForQuote && woForQuote.materialEta, woForQuote && woForQuote.scheduledStart, woForQuote && woForQuote.status, balance < 0.5]);

  const onStepPage = route === stepRoute;
  // step 9: is the job actually on the Crew Mode board (right crew + day)?
  const [crewJobShown, setCrewJobShown] = React.useState<any>(true);
  React.useEffect(() => {
    if (route !== "crew" || tour.step !== 9) return;
    const t = setInterval(() => setCrewJobShown(!!document.querySelector('[data-tour="crew-job"]')), 500);
    return () => clearInterval(t);
  }, [route, tour.step]);
  // step 2 coaching phase (after the trace): h → hold → gate → generate
  const coach = tour.coach || {};
  const hDone = !!(coach.h || est.houseFitAt > 0);
  const holdDone = !!(coach.hold || est.sideMenuAt > 0);
  const gateDone = !!(coach.gate || est.gates >= 1);
  const coachPhase = lf < 1 ? null : !hDone ? "h" : !holdDone ? "hold" : !gateDone ? "gate" : "generate";
  const pendingCO = leadQuote && (leadQuote.changeOrders || []).find((c: any) => c && c.status === "pending");
  const approvedCO = leadQuote && (leadQuote.changeOrders || []).find((c: any) => c && c.status === "approved");
  // spotlight targets per step + route
  const spot = !tour.dismissed && onStepPage ? (
    tour.step === 1 ? '[data-tour="lead-inbox"]'
    : tour.step === 2 ? (lf < 1 ? '[data-tour="address"]' : coachPhase === "generate" ? '[data-tour="generate"]' : null)
    : tour.step === 3 ? '[data-tour="pricing"]'
    : tour.step === 4 ? '[data-tour="signpay"]'
    : tour.step === 5 ? (route === "quote" ? '[data-tour="payments"]' : woForQuote ? `[data-wo="${woForQuote.id}"]` : null)
    : tour.step === 6 ? '[data-tour="supplies"]'
    : tour.step === 7 ? '[data-tour="auto-schedule"], [data-tour="schedule-board"]'
    : tour.step === 8 ? (approvedCO ? '[data-tour="readiness"]' : '[data-tour="readiness"]')
    : tour.step === 9 ? (stepRoute === "calendar" ? (woForQuote ? `[data-cal-wo="${woForQuote.id}"]` : '[data-tour="schedule-board"]') : '[data-tour="crew-job"]')
    : tour.step === 10 ? '[data-tour="photos"]'
    : tour.step === 11 ? (route === "quote" ? '[data-tour="payments"]' : '[data-tour="pricing"]')
    : tour.step === 12 ? (route === "workorders" ? '[data-tour="followup"]' : '[data-tour="cust-followup"]')
    : tour.step === 13 ? '[data-tour="labor-actuals"], [data-tour="reports-head"]'
    : null) : null;
  useSpotlight(spot, [spot]);

  const goStep = (n: number, phase?: string) => {
    const s = STEPS[n - 1];
    const patch: any = {};
    if (tour.fromRoute) patch.fromRoute = null;
    if (n === 9) patch.installPhase = phase || (phase === undefined && tour.installPhase === "crew" ? "crew" : "schedule");
    if (Object.keys(patch).length) setTour(patch);
    const r = n === 9 ? (patch.installPhase === "crew" ? "crew" : "calendar") : s.route;
    if (r === "estimator") go("estimator", { prefillCustomer: LEAD_C, quoteId: tour.quoteId || LEAD_Q });
    else if (r === "quote") go("quote", { quoteId: tour.quoteId || LEAD_Q });
    else if (r === "workorders") go("workorders", woForQuote ? { openWorkOrder: woForQuote.id } : null);
    else if (r === "crew") go("crew", woForQuote && woForQuote.scheduledStart ? { day: woForQuote.scheduledStart, crewId: woForQuote.crewId || "" } : null);
    else if (r === "calendar") go("calendar", woForQuote && woForQuote.scheduledStart ? { day: woForQuote.scheduledStart } : null);
    else go(r);
  };
  // jump anywhere: fast-forward every earlier step that isn't met, then go.
  // The work order is created by the app on the render after the deposit,
  // so a jump past step 5 with no work order yet parks at 5 and finishes
  // itself once the work order exists (pendingJump).
  const jumpTo = (n: number) => {
    for (let k = 3; k < n; k++) if (!readyFor(k, store, tour)) fastForward(k, store, tour);
    d.track("tour_jump", { to: n });
    if (n > 5 && !jobOf(store, tour).wo) { setTour({ step: 5, pendingJump: n, dismissed: false, done: false, fromRoute: null }); setList(false); goStep(5); return; }
    setTour({ step: n, pendingJump: null, dismissed: false, done: false, fromRoute: null, installPhase: n === 9 ? "schedule" : tour.installPhase });
    setList(false);
    goStep(n, n === 9 ? "schedule" : undefined);
  };
  React.useEffect(() => { if (tour.pendingJump && woForQuote) { const n = tour.pendingJump; setTour({ pendingJump: null }); jumpTo(n); } }, [tour.pendingJump, woForQuote && woForQuote.id]);
  const doItForMe = (n: number) => { fastForward(n, store, tour); d.track("tour_fast_forward", { step: n }); };
  const next = () => { if (tour.step < N) { const n = tour.step + 1; setTour({ step: n, fromRoute: null, installPhase: n === 9 ? "schedule" : tour.installPhase }); goStep(n, n === 9 ? "schedule" : undefined); } };
  const openSheet = (which: "crew" | "packet") => {
    const qp = d.quotePage;
    if (route === "quote" && qp) { (which === "crew" ? qp.crewSheet : qp.jobPacket)(); return; }
    setTour({ fromRoute: "quote" });
    go("quote", { quoteId: tour.quoteId || LEAD_Q, openSheet: which });
  };
  const scrollTo = (sel: string) => { try { const el = document.querySelector(sel); if (el) el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (e) {} };
  // get the sheet out of the way, put the target on screen and pulse it. "It's
  // highlighted in the panel" is not something a phone reader can act on when
  // the sheet is covering the panel.
  const revealTarget = (sel: string) => {
    if (narrow) setCollapsed(true);
    setTimeout(() => {
      try {
        const el: any = pickTarget(sel);
        if (!el) return;
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        el.classList.remove("demo-flash");
        void el.offsetWidth;                                  // restart it on a repeat tap
        el.classList.add("demo-flash");
        setTimeout(() => el.classList.remove("demo-flash"), 2600);
      } catch (e) {}
    }, narrow ? 280 : 0);                                     // let the collapse settle first
  };

  async function useSampleYard() {
    const y = DEMO_SAMPLE_YARDS[0];
    d.track("address_fallback_used", { yard: y.id, live: !!y.live });
    const e = d.estimator;
    if (!e) return;
    try { if (!y.live && e.loadSample) await e.loadSample(y); else await e.search(y.address); }
    catch (err) { notify("Couldn't load the sample yard — click the property on the map instead."); }
  }

  async function sendCustomerEmail(e: any) {
    e.preventDefault();
    const to = String(email || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(to)) { setSendState("error:Enter a valid email address."); return; }
    const q = leadQuote; if (!q) { setSendState("error:Open the quote first."); return; }
    const cust = leadCust;
    if (!cust) { setSendState("error:This quote has no customer."); return; }
    setSendState("sending");
    d.setEmail(to);
    d.track("email_captured", { branded: d.branded(store) ? "yes" : "no" });
    const co = store.company || {};
    const company = String(co.name || "").trim() && co.name !== d.seedCompany ? co.name : d.seedCompany;
    let token = cust.hubToken || null, sent = false, err = "";
    if (d.apiBase && d.server.enabled !== false && !d.server.busy) {
      try {
        const snapshot = { settings: store.settings, company: co, companyReady: true, customProducts: store.customProducts || [], customers: [cust], quotes: [q], workOrders: (store.workOrders || []).filter((w: any) => w.quoteId === q.id) };
        const r = await d.api("/send-quote", { email: to, quoteId: q.id, company, state: snapshot });
        token = r.hubToken || token; sent = true;
      } catch (e2: any) { err = String((e2 && e2.message) || "The email didn't go out."); }
    }
    if (!token) token = "demo-" + hex(12);
    if (cust.hubToken !== token) store.updateCustomer(cust.id, { hubToken: token });
    setTour({ step: 4, hubToken: token, emailed: sent });
    setSendState(sent ? "sent" : err ? "error:" + err : "local");
  }

  function lockFounder() {
    d.track("founder_cta_clicked", { plan: "pro" });
    const co = store.company || {};
    const company = co.name && co.name !== d.seedCompany ? co.name : "";
    try { const carry = d.carryState(company); if (carry) localStorage.setItem(d.promoteKey, JSON.stringify(carry)); } catch (e) {}
    try {
      const data = { plan: "pro", email: d.email || "", name: "", company, at: Date.now(), src: "demo" };
      const domain = /fenceworks\.ai$/.test(location.hostname) ? "; domain=.fenceworks.ai" : "";
      const secure = location.protocol === "https:" ? "; Secure" : "";
      document.cookie = "fw_handoff=" + encodeURIComponent(JSON.stringify(data)) + "; path=/; max-age=900; SameSite=Lax" + domain + secure;
    } catch (e) {}
    d.exit();
  }
  function addChangeOrder() {
    const q = leadQuote; if (!q) return;
    if ((q.changeOrders || []).some((c: any) => c && c.demoSample)) return;
    const perFt = q.lf ? (Number(q.total) || 0) / q.lf : 45;
    const amount = Math.round(perFt * 20);
    store.updateQuote(q.id, { changeOrders: [...(q.changeOrders || []), { id: "co-demo-" + Date.now(), kind: "custom", label: "Extra 20 ft found on site — run to the shed", amount, status: "pending", at: Date.now(), createdAt: Date.now(), photo: CO_PHOTO, demoSample: true }] });
    d.track("change_order_added");
  }
  function approveChangeOrder(co: any) {
    const c = leadQuote; if (!c || !co) return;
    const cos = (c.changeOrders || []).map((x: any) => x.id === co.id ? { ...x, status: "approved", approval: { name: c.customer, at: new Date().toISOString(), sandbox: true } } : x);
    const base = c.signedTotal != null ? Number(c.signedTotal) : Number(c.total) || 0;
    const sum = cos.filter((x: any) => x.status === "approved").reduce((a: number, x: any) => a + (Number(x.amount) || 0), 0);
    store.updateQuote(c.id, { changeOrders: cos, total: Math.round((base + sum) * 100) / 100 });
    d.track("change_order_approved", { via: "fast_forward" });
  }
  // step 12: the review + referral, for real (same buttons the work order has)
  function requestReview() { if (!woForQuote) return; store.updateWorkOrder(woForQuote.id, { reviewRequested: true, reviewRequestedAt: Date.now() }); d.track("review_requested"); }
  function mockReview() { if (!woForQuote) return; store.updateWorkOrder(woForQuote.id, { reviewReceived: true, reviewReceivedAt: Date.now(), reviewText: "Crew showed up when they said, fence was done in a day and the yard was spotless after. The online quote + paying from my phone was the easiest part of the whole project.", reviewStars: 5 }); d.track("review_mocked"); }
  function requestReferral() { if (!woForQuote) return; store.updateWorkOrder(woForQuote.id, { referralRequested: true, referralRequestedAt: Date.now() }); d.track("referral_requested"); }
  const seqPreview = (id: string) => {
    const own = store.settings && Array.isArray(store.settings.sequences) && store.settings.sequences.length ? store.settings.sequences : [];
    const seqs = own.length ? own : (((window as any).VFP && (window as any).VFP.DEFAULT_SETTINGS && (window as any).VFP.DEFAULT_SETTINGS.sequences) || []);
    const sq = seqs.find((x: any) => x.id === id); if (!sq || !sq.steps || !sq.steps[0]) return null;
    const r = (window as any).renderTemplate || ((t: string) => t);
    const ctx = { customer: custFirst, company: (store.company && store.company.name) || d.seedCompany, reviewLink: "https://g.page/r/your-review-link", review_link: "https://g.page/r/your-review-link", address: leadQuote && leadQuote.address, reward: "$100 Visa gift card" };
    const st = sq.steps[0];
    return <div className="seq-preview-box" style={{ marginTop: 8 }}><div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 4 }}>{r(st.subject, ctx)}</div><div className="faint" style={{ whiteSpace: "pre-wrap", fontSize: 11.5, maxHeight: 120, overflow: "auto" }}>{r(st.body, ctx)}</div><div className="faint" style={{ fontSize: 10.5, marginTop: 4 }}>{sq.steps.length} emails · day {sq.steps.map((x: any) => x.delayDays || 0).join(" → ")} · edit under Settings → Email Sequences</div></div>;
  };
  const hubToken = tour.hubToken || (leadCust && leadCust.hubToken);
  const ready = readyFor(tour.step, store, tour);
  const NextBtn = ({ label }: any) => <button className="btn btn-primary" onClick={next}>{label || "Next →"}</button>;
  const Prereq = ({ what, n }: any) => (
    <>
      <p className="faint">This step needs {what} first.</p>
      <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={() => doItForMe(n)}><Icon name="bolt" size={13} />Do it for me</button><button className="btn btn-ghost btn-sm" onClick={() => jumpTo(Math.max(1, n - 1))}>Go back a step</button></div>
    </>
  );
  const stepPage = (GUIDE[stepRoute] && GUIDE[stepRoute].title) || stepRoute;
  const takeMeThere = (<>
    <p className="faint">This step happens on the {stepPage} page.</p>
    <div className="demo-card-actions"><button className="btn btn-primary" onClick={() => goStep(tour.step)}>Take me there</button></div>
  </>);
  const Coach = ({ n, on, done, children }: any) => <div className={"demo-coach-row" + (on ? " on" : "") + (done ? " done" : "")}><span className="demo-coach-n">{done ? "✓" : n}</span><div>{children}</div></div>;

  // ---------- the card body per step ----------
  let body: any = null;
  let wide = false;   // the card grows when it shows the customer's phone
  // "here" = on the step's page, or still on the page where the step just
  // advanced by itself (paid on the portal → step 5 shows on the quote page)
  const here = onStepPage || (!!tour.fromRoute && route === tour.fromRoute);
  const s = tour.step;
  if (!here && GUIDE[route] && !(route === "dashboard" && s === 1)) {
    body = <PageContext route={route} step={step} onBack={() => goStep(s)} />;
  } else if (s === 1) {
    body = here ? (<>
      <p>This came in through the widget on your website <b>11 minutes ago</b>. {custFirst} wants about 180 ft of privacy fence with one gate. Let's quote it before anyone else calls her back.</p>
      <p className="faint">The timer on the lead is the only KPI that matters here: under 15 minutes wins most of these.</p>
      <div className="demo-card-actions"><button className="btn btn-primary" onClick={() => { setTour({ step: 2 }); goStep(2); }}><Icon name="map" size={14} />Quote it →</button></div>
    </>) : takeMeThere;
  } else if (s === 2) {
    const e = d.estimator;
    body = here ? (lf >= 1 ? (<>
      <p><b>{Math.round(lf)} ft</b> traced — the price on the left is live. Now the three moves that make this fast:</p>
      <div className="demo-coach">
        <Coach n={1} on={coachPhase === "h"} done={hDone}>
          {est.house ? <>The house is <b>auto-detected</b> (amber outline). Press <kbd>H</kbd> — the fence snaps to the house sides so the run ends at the wall, not past it.</> : <>Press <kbd>H</kbd> to snap the fence to the house sides when a house outline is detected on the lot. This yard doesn't have one — skip ahead.</>}
          {coachPhase === "h" && <div className="demo-card-actions" style={{ marginTop: 6 }}>
            {est.house && <button className="btn btn-outline btn-sm" onClick={() => { e && e.fitToHouse && e.fitToHouse(); setTour({ coach: { ...coach, h: true } }); }}><Icon name="bolt" size={12} />Do it for me</button>}
            <button className="btn btn-ghost btn-sm" onClick={() => setTour({ coach: { ...coach, h: true } })}>Skip</button>
          </div>}
        </Coach>
        <Coach n={2} on={coachPhase === "hold"} done={holdDone}>
          <b>Press and hold</b> (or right-click) a side's ft label or a corner — the wall's own menu: split, extend, square up, delete, set the grade.
          {coachPhase === "hold" && <div className="demo-card-actions" style={{ marginTop: 6 }}><button className="btn btn-ghost btn-sm" onClick={() => setTour({ coach: { ...coach, hold: true } })}>Skip</button></div>}
        </Coach>
        <Coach n={3} on={coachPhase === "gate"} done={gateDone}>
          <b>Add a gate:</b> double-click a side, or press <kbd>G</kbd>. Gates price by size and add install hours to the schedule.
          {coachPhase === "gate" && <div className="demo-card-actions" style={{ marginTop: 6 }}>
            <button className="btn btn-outline btn-sm" onClick={() => { e && e.addGate && e.addGate(); setTour({ coach: { ...coach, gate: true } }); }}><Icon name="plus" size={12} />Add one for me</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setTour({ coach: { ...coach, gate: true } })}>Skip</button>
          </div>}
        </Coach>
      </div>
      {coachPhase === "generate" && <>
        <p>Tick tear-out or the permit fee if you like, then hit <b>Generate Estimate</b> — in the panel on a desktop, pinned to the bottom of the screen on a phone.</p>
        <div className="demo-card-actions">
          <button className="btn btn-primary btn-sm" onClick={() => revealTarget('[data-tour="generate"]')}><Icon name="send" size={13} />Show me Generate Estimate</button>
        </div>
      </>}
      <details className="demo-more">
        <summary>Want to see every feature?</summary>
        <div className="demo-card-actions" style={{ marginTop: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => e && e.showKeys && e.showKeys()}><kbd>?</kbd> All shortcuts</button>
          <button className="btn btn-ghost btn-sm" onClick={() => e && e.gradeMode && e.gradeMode(true)}><Icon name="ruler" size={12} />Grade points</button>
          <button className="btn btn-ghost btn-sm" onClick={() => e && e.showKeys && e.showKeys()}><Icon name="help" size={12} />More keys</button>
        </div>
        <p className="faint" style={{ margin: "6px 0 0" }}><kbd>B</kbd> style · <kbd>V</kbd> vinyl · <kbd>[</kbd> <kbd>]</kbd> rotate · <kbd>S</kbd> split · <kbd>E</kbd> extend · <kbd>Q</kbd> square up · <kbd>F</kbd> fit lot line · <kbd>L</kbd> lot line</p>
      </details>
    </>) : (<>
      <p><b>Type any address — try your own house.</b> Pick it from the list and the lot outlines itself. Click a side to include it in the fence, drag the gold corners.</p>
      <p className="faint">No match, or no parcel where you are? Use the sample yard — same flow, same math.</p>
      <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={useSampleYard}><Icon name="mappin" size={13} />Use the sample yard</button></div>
    </>)) : takeMeThere;
  } else if (s === 3) {
    body = !ready ? <Prereq what="a traced fence" n={3} /> : here ? (<>
      <p>This is <b>your</b> side of the quote: {total} to the customer, with material, labor, markup and margin broken out below it. <b>Edit estimate</b> changes style, gates, tear-out and the permit line; <b>Open on map</b> reloads the drawing.</p>
      <BrandingFields store={store} />
      <div className="demo-card-actions"><NextBtn label={`See what ${custFirst} gets →`} /></div>
    </>) : takeMeThere;
  } else if (s === 4) {
    const sent = sendState === "sent" || (tour.hubToken && tour.emailed);
    wide = !!hubToken && !narrow;
    body = !readyFor(3, store, tour) ? <Prereq what="a traced fence" n={3} /> : !hubToken ? <p className="faint">Setting up {custFirst}'s portal…</p> : (<>
      <p><b>How it gets to her:</b> <b>Sign &amp; pay link</b> (highlighted up top) texts or emails {custFirst} a private link. What opens on her phone is exactly this — scope, price, e-sign, and the deposit button. No login, no app.</p>
      {sent && <p className="faint"><b>Sent to your inbox.</b> Open it on your phone — that's the customer experience.</p>}
      {sendState.startsWith("error:") && <p style={{ color: "var(--amber)", fontSize: 12.5 }}>{sendState.slice(6)} The customer view still works below.</p>}
      <PhoneFrame token={hubToken} compact={narrow} />
      <p className="faint" style={{ margin: "8px 0 0" }}>{custFirst} already asked about the deposit from her portal — <a href="#" onClick={(ev) => { ev.preventDefault(); go("messages"); }}>see the thread on Messages</a>. Go ahead — sign it and pay the deposit <b>as the customer</b>. No card is charged.</p>
      <details className="demo-more">
        <summary>Email me a copy of what she gets</summary>
        <form onSubmit={sendCustomerEmail} style={{ marginTop: 6 }}>
          <div className="row gap-8">
            <input className="input" type="email" required placeholder="you@yourfenceco.com" value={email} onChange={(ev) => setEmail(ev.target.value)} autoComplete="email" style={{ flex: 1 }} />
            <button className="btn btn-outline btn-sm" type="submit" disabled={sendState === "sending"}>{sendState === "sending" ? "Sending…" : "Send it"}</button>
          </div>
          <p className="faint" style={{ margin: "6px 0 0" }}>One email, to you only. No list, no sequence you didn't ask for.</p>
        </form>
      </details>
      <div className="demo-card-actions"><button className="btn btn-ghost btn-sm" onClick={() => doItForMe(5)}>Skip — mark it signed &amp; paid</button></div>
    </>);
  } else if (s === 5) {
    body = !ready ? <Prereq what="the deposit paid on the portal" n={5} /> : (<>
      <p><b>{custFirst} signed and paid the deposit.</b> The estimate is now an <b>invoice</b> (see the header) and the <b>Deposit &amp; payments</b> block below is where the money lives:</p>
      <ul className="demo-how">
        <li>Online payments land in <b>your own Stripe</b> — connect it once under Integrations.</li>
        <li>Checks and cash: <b>Record payment</b> puts them on the same ledger.</li>
        <li>Financing through <b>Wisetack</b> or your own lender shows as an option on her portal.</li>
        <li>The balance gets its own pay link when the job's done.</li>
      </ul>
      <p className="faint">On your side the job is now a work order: materials pulled from the estimate, crew assigned, first stop is ordering supplies.</p>
      <div className="demo-card-actions">
        {route === "quote" && <button className="btn btn-ghost btn-sm" onClick={() => scrollTo('[data-tour="payments"]')}><Icon name="dollar" size={12} />Show the payments block</button>}
        <NextBtn label="Next: order materials →" />
      </div>
    </>);
  } else if (s === 6) {
    body = !ready ? <Prereq what="a work order" n={5} /> : here ? (<>
      <p><b>Order supplies.</b> Everything the job needs is listed here from the estimate — panels, posts, concrete, gate hardware, fasteners — <b>grouped by supplier</b> once you set a supplier per product.</p>
      <p>Flip <b>Supplies ordered</b> and set the delivery date your supplier gave you, or <b>Create purchase order</b> for a numbered PO you can email. Receive it as trucks arrive and inventory updates itself.</p>
      <p className="faint">That delivery date is what lets the job schedule itself in the next step. Meanwhile the AI receptionist booked Carlos for Thursday — <a href="#" onClick={(e) => { e.preventDefault(); go("ai"); }}>see the call →</a></p>
      <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={() => doItForMe(6)}><Icon name="bolt" size={13} />Do it for me — ETA in 3 days</button></div>
    </>) : takeMeThere;
  } else if (s === 7) {
    body = !ready ? <Prereq what="supplies ordered with an ETA" n={6} /> : here ? (<>
      <p><b>It schedules itself.</b> Materials land {woForQuote && woForQuote.materialEta ? fmtDay(woForQuote.materialEta) : "soon"}; the job goes on the board the next open crew workday after that. Drag it to change the day or the crew.</p>
      <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={() => doItForMe(7)}><Icon name="bolt" size={13} />Auto-schedule it for me</button></div>
    </>) : takeMeThere;
  } else if (s === 8) {
    wide = !!(approvedCO && hubToken && !narrow);
    body = !ready ? <Prereq what="the job on the schedule" n={7} /> : here ? (<>
      <p><b>Site readiness.</b> Site visit confirmed, permit pulled, 811 locate called. Nothing digs until the three are checked — this is where jobs actually get delayed.</p>
      {!pendingCO && !approvedCO && <p className="faint">Found extra footage on the site visit? Add a change order — with a photo of why — and {custFirst} approves it on her portal. The contract re-foots and the invoice updates.</p>}
      {pendingCO && <p className="faint"><b>Change order pending:</b> {pendingCO.label} · +{money0(pendingCO.amount)}{pendingCO.photo ? " · photo attached" : ""}. {custFirst} sees an "Approve this change" card on her portal with the photo and signs it.</p>}
      {approvedCO && <p className="faint"><b>Approved</b> by {custFirst}: +{money0(approvedCO.amount)}. Her portal and the invoice now read <b>{total}</b> — the original contract stays on the document with the change itemized under it:</p>}
      {approvedCO && hubToken && <PhoneFrame token={hubToken} compact={narrow} />}
      <p style={{ marginTop: 8 }}><b>The crew's plan, before they roll:</b> the <b>crew sheet</b> is the numbered build sequence with the bill of materials to load the truck (nothing forgotten); the <b>job packet</b> is the blueprint fence plan and install specs the customer and the crew both get.</p>
      <div className="demo-card-actions">
        <button className="btn btn-ghost btn-sm" onClick={() => openSheet("crew")}><Icon name="clipboard" size={13} />Open the crew sheet</button>
        <button className="btn btn-ghost btn-sm" onClick={() => openSheet("packet")}><Icon name="book" size={13} />Open the job packet</button>
      </div>
      <div className="demo-card-actions">
        <button className="btn btn-outline btn-sm" onClick={() => doItForMe(8)}><Icon name="check" size={13} />Mark all three ready</button>
        {!pendingCO && !approvedCO && <button className="btn btn-ghost btn-sm" onClick={addChangeOrder}><Icon name="plus" size={13} />Add a change order (+20 ft, with photo)</button>}
        {pendingCO && <button className="btn btn-ghost btn-sm" onClick={() => approveChangeOrder(pendingCO)}>{custFirst} approves it (mock)</button>}
        {approvedCO && <button className="btn btn-ghost btn-sm" onClick={() => go("quote", { quoteId: tour.quoteId || LEAD_Q })}><Icon name="clipboard" size={13} />See the invoice</button>}
      </div>
      {readyFor(9, store, tour) && <div className="demo-card-actions"><NextBtn label="Next: install day →" /></div>}
    </>) : takeMeThere;
  } else if (s === 9) {
    const dayLabel = woForQuote && woForQuote.scheduledStart ? fmtDay(woForQuote.scheduledStart) : "";
    body = !ready ? <Prereq what="readiness checked" n={8} /> : !here ? takeMeThere : stepRoute === "calendar" ? (<>
      <p><b>Install day{dayLabel ? " — " + dayLabel : ""}.</b> First the schedule: the job is on the board (highlighted) for {dayLabel || "its day"}, on its crew's lane. Drag it to another day or crew and the customer's portal updates.</p>
      <p className="faint">Google Calendar sync pushes it to the crew's phones; the day view shows appointment times.</p>
      <div className="demo-card-actions"><button className="btn btn-primary" onClick={() => { setTour({ installPhase: "crew" }); goStep(9, "crew"); }}><Icon name="phone" size={14} />Now the crew's phone: Crew Mode →</button></div>
    </>) : (<>
      <p><b>Crew Mode</b> — the crew lead's phone. Navigate to the job, <b>Start job</b> texts {custFirst} that the crew is on the way, and <b>Clock in</b> logs hours to the job.</p>
      <p><b>Clock in is GPS-fenced:</b> it only works within 0.1 mi of the site (you set the distance). Tap it now — you're not in Suamico, so it'll stop you and offer the <b>office override</b> with a reason. Either way the fix, the distance and the override land on the hours sheet, where the office can adjust any entry with the original kept.</p>
      {woForQuote && woForQuote.scheduledStart && !crewJobShown && <p className="faint">Nothing on the board? The job is on {dayLabel} — <a href="#" onClick={(e) => { e.preventDefault(); goStep(9, "crew"); }}>show that day</a>.</p>}
      <div className="demo-card-actions">
        <button className="btn btn-ghost btn-sm" onClick={() => { setTour({ installPhase: "schedule" }); goStep(9, "schedule"); }}>← Schedule</button>
        <button className="btn btn-outline btn-sm" onClick={() => doItForMe(9)}><Icon name="bolt" size={13} />Start the job for me</button>
      </div>
    </>);
  } else if (s === 10) {
    body = !ready ? <Prereq what="the job in progress" n={9} /> : here ? (<>
      <p><b>Photos, then done.</b> Before and after shots live on the job — they go on the customer's portal, and the after shots feed the review and social posts. Mark it completed and the follow-up starts.</p>
      <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={() => doItForMe(10)}><Icon name="image" size={13} />Add sample photos &amp; complete</button></div>
    </>) : takeMeThere;
  } else if (s === 11) {
    wide = balance >= 0.5 && !!hubToken && !narrow;
    body = !ready ? <Prereq what="the job completed" n={10} /> : balance >= 0.5 ? (<>
      <p><b>Final payment.</b> {custFirst} owes {money0(balance)}. Her portal now says "Pay your balance" — card or bank transfer, straight to your Stripe. Or record the check she hands you under <b>Deposit &amp; payments</b>.</p>
      {hubToken && <PhoneFrame token={hubToken} compact={narrow} />}
      <div className="demo-card-actions">
        {route === "quote" && <button className="btn btn-ghost btn-sm" onClick={() => scrollTo('[data-tour="payments"]')}><Icon name="dollar" size={12} />Show the payments block</button>}
        <button className="btn btn-ghost btn-sm" onClick={() => doItForMe(11)}>Skip — she paid the balance</button>
      </div>
    </>) : (<>
      <p><b>Paid in full.</b> {total} collected, deposit and balance — the invoice header now reads <b>Invoice · paid</b>. QuickBooks gets the invoice and both payments when it's connected.</p>
      <p className="faint">Everything that just happened — quote, sends, signature, payments, change order, crew hours, photos — is stamped on the <b>Job timeline</b> at the bottom of this page{route === "quote" ? " " : " (and on the work order)"}.</p>
      <div className="demo-card-actions">
        {route === "quote" && <button className="btn btn-ghost btn-sm" onClick={() => scrollTo('[data-tour="timeline"]')}><Icon name="activity" size={12} />Show the timeline</button>}
        <NextBtn label="Next: the review →" />
      </div>
    </>);
  } else if (s === 12) {
    const w = woForQuote;
    body = !readyFor(11, store, tour) ? <Prereq what="the job completed" n={10} /> : !here ? takeMeThere : (<>
      <p><b>Review + referral.</b> The day a job closes, the <b>Job complete</b> panel on the work order (highlighted) runs the after-sale: review request, referral ask with a $100 bonus, warranty + checkup visit. Same panel on {custFirst}'s customer profile.</p>
      {w && !w.reviewRequested && <>
        <p className="faint">Step one — the review request. This is the email that goes out (edit it under Settings → Email Sequences):</p>
        {seqPreview("seq-review")}
        <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={requestReview}><Icon name="send" size={13} />Send the review request</button></div>
      </>}
      {w && w.reviewRequested && !w.reviewReceived && <>
        <p className="faint"><b>Sent.</b> Day 5 a nudge follows if she hasn't posted. Most customers do it from the link the same evening.</p>
        <div className="demo-card-actions"><button className="btn btn-outline btn-sm" onClick={mockReview}>★ {custFirst} leaves 5 stars (mock)</button></div>
      </>}
      {w && w.reviewReceived && <div className="demo-review">
        <div className="row between"><b style={{ fontSize: 13 }}>{leadQuote && leadQuote.customer}</b><span style={{ color: "var(--amber)", letterSpacing: 1 }}>★★★★★</span></div>
        <p className="faint" style={{ margin: "4px 0 0", fontStyle: "italic" }}>“{w.reviewText || "Crew showed up when they said, fence was done in a day and the yard was spotless after."}”</p>
        <div className="faint" style={{ fontSize: 10.5, marginTop: 4 }}>Google · just now · counted on your Business Health page</div>
      </div>}
      {w && w.reviewReceived && !w.referralRequested && <>
        <p className="faint" style={{ marginTop: 8 }}>Step two — the referral ask, 4 emails over 90 days with her own referral link. Leads from it land on her profile with the $100 tracked:</p>
        {seqPreview("seq-referral")}
        <div className="demo-card-actions"><button className="btn btn-primary btn-sm" onClick={requestReferral}><Icon name="user" size={13} />Send the referral ask</button></div>
      </>}
      {w && w.referralRequested && <p className="faint"><b>Referral ask sent.</b> Both are logged on the job timeline and on {custFirst}'s profile — <a href="#" onClick={(e) => { e.preventDefault(); go("customers", { customerId: LEAD_C }); }}>open her profile</a>.</p>}
      <div className="demo-card-actions"><NextBtn label="Last: how the business is doing →" /></div>
    </>);
  } else if (s === 13) {
    const w = woForQuote;
    const crews = (store.settings && store.settings.crews) || [];
    const est = w ? woEstLabor(w, store, crews) : { hours: 0, cost: 0 };
    const act = w ? actualHours(w) : 0;
    const rate = w ? crewRate(crews.find((c: any) => c.id === w.crewId)) : 0;
    const dH = act - est.hours;
    body = !readyFor(12, store, tour) ? <Prereq what="the job paid in full" n={11} /> : !here ? takeMeThere : (<>
      <p><b>Business Health.</b> Every job you just ran feeds this page: close rate, revenue, pipeline, why you lose, and the one owners skip — <b>labor, estimated vs clocked</b> (highlighted).</p>
      {w && act > 0 && <div className="demo-review" style={{ borderColor: "var(--line-2)" }}>
        <div className="row between" style={{ fontSize: 13 }}><b>{leadQuote && leadQuote.customer}</b><span className="tnum" style={{ color: dH > 0.01 ? "var(--amber)" : "var(--green)", fontWeight: 700 }}>{dH >= 0 ? "+" : ""}{dH.toFixed(1)} hr · {dH >= 0 ? "+" : "−"}{money0(Math.abs(dH * rate))}</span></div>
        <p className="faint" style={{ margin: "4px 0 0" }}>Quoted {est.hours.toFixed(1)} crew-hrs, the crew clocked {act.toFixed(1)}. The note on the entry says why (the run to the shed, a latch swap). The invoice's internal breakdown shows the actual margin next to the quoted one, and this page adds it up across every job — that's how you know when the labor settings need a nudge.</p>
      </div>}
      <p className="faint">Also here: the review count and referral leads on Sales Team, Budget &amp; Cash for the four buckets and the revenue goal, and the TV display for the shop wall.</p>
      <div className="demo-end">
        <p className="faint" style={{ margin: 0, textAlign: "center" }}>That's the whole job — lead to review — in one place.</p>
        <button className="btn btn-primary btn-block" onClick={() => { try { document.querySelector<HTMLButtonElement>(".demo-bar .btn-primary")?.click(); } catch (e) {} }}><Icon name="check" size={14} />Keep this as my free workspace</button>
        <button className="btn btn-outline btn-block" onClick={lockFounder}><Icon name="bolt" size={14} />Lock founder pricing — 50% off forever</button>
        <button className="btn btn-ghost btn-sm btn-block" onClick={() => { d.track("widget_snippet_viewed"); setSnippet(true); }}><Icon name="clipboard" size={13} />Install the lead widget on my site</button>
        <p className="faint" style={{ margin: "6px 0 0", textAlign: "center" }}>{founderLine()} Free plan is free forever.</p>
      </div>
    </>);
  }

  if (here && !onStepPage && body) {
    // the jump goes AFTER the step's copy — you read what the step is about,
    // then act. It's the primary action while you're on the wrong page, so it
    // carries the teal; the card's own actions step down to outline (one teal
    // per card, enforced in CSS by .demo-card-offpage).
    body = (<>
      <div className="demo-offpage">{body}</div>
      <div className="demo-card-actions"><button className="btn btn-primary btn-block" onClick={() => goStep(s)}><Icon name="chevright" size={13} />Open {stepPage}</button></div>
    </>);
  }

  const stepList = (
    <div className="demo-steps">
      {STEPS.map((st) => (
        <button key={st.n} className={"demo-step" + (st.n === tour.step ? " on" : "") + (st.n < tour.step ? " done" : "")} onClick={() => jumpTo(st.n)}>
          <span className="demo-step-n">{st.n}</span><span>{st.title}</span>
        </button>
      ))}
    </div>
  );

  if (tour.dismissed) {
    return (<>
      <div className="demo-resume-wrap" style={pillDrag.style} data-drag-panel ref={pillDrag.panelRef}>
        <span className="demo-grip" {...pillDrag.handleProps} title="Drag to move">⋮⋮</span>
        <button className="demo-resume" onClick={() => setTour({ dismissed: false })}><Icon name="bolt" size={13} />Walkthrough · step {tour.step} of {N}</button>
      </div>
      {snippet && <WidgetSnippetModal onClose={() => setSnippet(false)}
        onKeep={() => { setSnippet(false); try { document.querySelector<HTMLButtonElement>(".demo-bar .btn-primary")?.click(); } catch (e) {} }}
        onFounder={() => { setSnippet(false); lockFounder(); }} />}
    </>);
  }

  return (
    <>
      <div className={"demo-card" + (narrow ? " sheet" : "") + (narrow && actionBarH ? " lifted" : "") + (collapsed ? " collapsed" : "") + (wide ? " wide" : "")} role="dialog" aria-label="Sandbox walkthrough"
        style={narrow && actionBarH ? { ...cardDrag.style, bottom: actionBarH } : cardDrag.style} data-drag-panel ref={cardDrag.panelRef}>
        <div className="demo-card-head" {...cardDrag.handleProps} title={narrow ? undefined : "Drag to move the walkthrough"}>
          {!narrow && <span className="demo-grip" style={{ width: 14 }}>⋮⋮</span>}
          <button className="demo-steps-toggle" onClick={() => setList((v: boolean) => !v)} aria-expanded={list} title="Jump to any step"><Icon name="layers" size={12} />Step {tour.step} of {N} ▾</button>
          {cardDrag.moved && <button className="demo-card-x" style={{ marginLeft: "auto" }} onClick={cardDrag.reset} title="Back to the corner">Reset</button>}
          {collapsed
            ? <button className="demo-card-x" style={cardDrag.moved ? { marginLeft: 0 } : undefined} onClick={() => setCollapsed(false)}>Show ▴</button>
            : <button className="demo-card-x" style={cardDrag.moved ? { marginLeft: 0 } : undefined} onClick={() => { d.track("tour_dismissed", { step: tour.step }); setTour({ dismissed: true }); }} title="Minimize — the walkthrough stays one tap away">Minimize</button>}
        </div>
        {list && stepList}
        {collapsed
          ? <p className="demo-card-collapsed" onClick={() => setCollapsed(false)}><b>{Math.round(lf)} ft traced.</b> Tap <b>Generate Estimate</b> below when the fence looks right.{coachPhase === "generate" && <> <button className="demo-card-point" onClick={(ev) => { ev.stopPropagation(); revealTarget('[data-tour="generate"]'); }}>Show me</button></>}</p>
          : (<>
              <h3 className="display">{step.title}{s === 9 ? (stepRoute === "crew" ? " · Crew Mode" : " · Schedule") : ""}</h3>
              <div className="demo-card-body">{body}</div>
            </>)}
      </div>
      {snippet && <WidgetSnippetModal onClose={() => setSnippet(false)}
        onKeep={() => { setSnippet(false); try { document.querySelector<HTMLButtonElement>(".demo-bar .btn-primary")?.click(); } catch (e) {} }}
        onFounder={() => { setSnippet(false); lockFounder(); }} />}
    </>
  );
}

Object.assign(window, { DemoTour });
export { DemoTour, STEPS as DEMO_TOUR_STEPS };
